import { Link } from '@tanstack/react-router';
import { useTranslation } from '@eon/i18n';
import { Button } from '@eon/ui';
import type { Station } from '@eon/types';
import { summarizeStationDevices } from '@/lib/station-map';

interface StationMapPopupProps {
  station: Station;
  showDetailLink?: boolean;
}

export function StationMapPopup({ station, showDetailLink = true }: StationMapPopupProps) {
  const { t } = useTranslation();
  const stats = summarizeStationDevices(station);

  return (
    <div className="space-y-3 text-sm">
      <div>
        <p className="font-semibold">{station.name}</p>
        {station.address ? (
          <p className="text-muted-foreground mt-0.5 text-xs">{station.address}</p>
        ) : null}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-md bg-emerald-500/10 px-2 py-1.5">
          <p className="text-lg font-semibold text-emerald-600">{stats.available}</p>
          <p className="text-muted-foreground text-[10px] uppercase tracking-wide">
            {t('map.availableDevices')}
          </p>
        </div>
        <div className="rounded-md bg-sky-500/10 px-2 py-1.5">
          <p className="text-lg font-semibold text-sky-600">{stats.inUse}</p>
          <p className="text-muted-foreground text-[10px] uppercase tracking-wide">
            {t('map.inUseDevices')}
          </p>
        </div>
        <div className="rounded-md bg-muted px-2 py-1.5">
          <p className="text-lg font-semibold">{stats.total}</p>
          <p className="text-muted-foreground text-[10px] uppercase tracking-wide">
            {t('map.totalDevices')}
          </p>
        </div>
      </div>
      {showDetailLink ? (
        <Button asChild size="sm" className="w-full">
          <Link to="/stations/$stationId" params={{ stationId: station.id }}>
            {t('station.detail.view')}
          </Link>
        </Button>
      ) : null}
    </div>
  );
}
